<template>
  <section class="analytics-product-table">
    <!-- analytics-outside-use -->
    <div class="sticky-header">
      <!-- Header / Filters -->
      <div class="list-header">
        <div class="ui-search-wrap">
          <Search @onenter="onSearch" :disabled="is_loading" :placeholder_text="search_placeholder" />
        </div>
        <div class="list-btn-box">
          <DropdownBrands
            v-if="start_date && end_date &&
            store.state.user_data?.merchant_account?.is_enable_multi_brand
            && label !== 'Advertisers'"
            :start_date="start_date"
            :end_date="end_date"
            :direction_type="'right'"
          />
          <DropdownAd
            v-if="start_date && end_date && label === 'Advertisers'"
            :start_date="start_date"
            :end_date="end_date"
            :direction_type="'right'"
          />
          <Dropdown
            v-if="creator_type_items.length && !is_link_only"
            :items="creator_type_items"
            :direction_type="'right'"
            :no_icon="true"
            :other_text="'Type: '"
            @update-dropdown="onCreatorTypeChange"
          />
          <Dropdown
            v-if="!hide_sort"
            :key="sort_index"
            :items="sort_items"
            :direction_type="'right'"
            :icon="'filter'"
            :default_value="sort_index"
            :other_text="'Sort: '"
            @update-dropdown="onSortChange"
          />
          <FilterDropdown
            v-if="hide_sort"
            :params="filter_params"
            @update-filter="updateFilter"
          />
          <el-button
            class="the-curated-btn"
            @click="toggleCurated"
            v-if="now_merchant.is_enable_storefront_curation && !is_link_only && !hide_curated"
          >
            <Checkbox size="small" :checked="curated_check" />
            Curated only
          </el-button>
          <Dropdown
            :items="per_page_items"
            :direction_type="'right'"
            :no_icon="true"
            :other_text="''"
            @update-dropdown="onPerPageChange"
          />
          <Pagenavi
            :disabled="last_page === 1"
            :page_current="current_page"
            :page_total="last_page"
            @goto-page="gotoPage"
          />
        </div>
      </div>

      <!-- Stats bar -->
      <div class="short-link" v-if="data_statistic && label !== 'Creators'">
        <div class="report-box">
          <Loading v-if="is_loading" class="absolute" />
          <div class="report-item">
            <div class="datainfo-wrap">
              <div class="value">
                {{
                  data_statistic.store_front_count != null
                    ? data_statistic.store_front_count.toLocaleString('en-US')
                    : '---'
                }}
              </div>
              <div class="text">Total Products</div>
            </div>
          </div>
          <div class="report-item" v-if="show_views">
            <SValue
              :chart_type_text="'views'"
              :is_wrap="true"
              :sum="data_statistic.product_views_sum"
              :value_text="'Product Views'"
              :show_sortlink="false"
              :sort_value="data_statistic.short_link_views_sum || 0"
              :store_value="data_statistic.views_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'click'"
              :is_wrap="true"
              :sum="data_statistic.all_click"
              :value_text="'Clicks'"
              :show_sortlink="sortlink_data === 'all' && data_statistic.can_toggle_sortlink"
              :sort_value="data_statistic.short_link_click_sum || 0"
              :store_value="data_statistic.click_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'tran'"
              :is_wrap="true"
              :sum="data_statistic.all_sales"
              :value_text="'Sales'"
              :show_sortlink="sortlink_data === 'all' && data_statistic.can_toggle_sortlink"
              :sort_value="data_statistic.short_link_sales_sum || 0"
              :store_value="data_statistic.sales_sum || 0"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'sales'"
              :is_wrap="true"
              :sum="data_statistic.all_sales_value"
              :value_text="'Sales Value'"
              :show_sortlink="sortlink_data === 'all' && data_statistic.can_toggle_sortlink"
              :sort_value="Math.ceil(data_statistic.short_link_sales_value_sum || 0)"
              :store_value="Math.ceil(data_statistic.sales_value_sum || 0)"
            />
          </div>
          <div class="report-item">
            <SValue
              :chart_type_text="'earnings'"
              :is_wrap="true"
              :sum="data_statistic.all_earnings"
              :value_text="'Earnings'"
              :show_sortlink="sortlink_data === 'all' && data_statistic.can_toggle_sortlink"
              :sort_value="Math.ceil(data_statistic.short_link_earnings_sum || 0)"
              :store_value="Math.ceil(data_statistic.earnings_sum || 0)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <Loading v-if="is_loading && !data_statistic" class="static" />

    <!-- Table -->
    <Scrollbar class="data-list-scroll" :show_bar="true">
      <div v-if="data.length !== 0" class="product-table-view">
        <Loading v-if="is_loading" class="absolute" />
        <Table>
          <template #head>
            <tr>
              <th class="name-row">
                <span>{{ label }} ({{ total.toLocaleString('en-US') }})</span>
              </th>
              <TableTh
                v-if="label !== 'Creators'"
                name="creator_count"
                title="Creators"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Creators"
              />
              <TableTh
                name="total_products"
                title="Total Products"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Total Products"
              />
              <TableTh
                v-if="show_views"
                name="views"
                title="Product Views"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Product Views"
              />
              <TableTh
                name="click"
                title="Product Clicks"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Product Clicks"
              />
              <TableTh name="sales" title="Sales" :sort_value="sort" @sort="onColumnSort" content="Sales" />
              <TableTh
                name="sales_value"
                title="Sales Value"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Sales Value"
              />
              <TableTh
                name="earnings"
                title="Earnings"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Earnings"
              />
              <TableTh
                v-if="label === 'Creators'"
                name="last_update"
                title="Last Update"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Last Update"
              />
            </tr>
          </template>

          <template #body>
            <tr v-for="(item, index) in data" :key="index" @click.stop="openDetail(item)">
              <td class="name-row">
                <template v-if="label === 'Products'">
                  <div class="product-name-cell">
                    <div
                      class="product-thumb"
                      :style="{ backgroundImage: `url(${item.image_url || 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?q=80&w=870&auto=format&fit=crop'})`, backgroundSize: 'cover', backgroundPosition: 'center' }"
                    ></div>
                    <div class="product-info">
                      <div class="product-title">{{ item.name }}</div>
                      <div class="product-brand">{{ item.brand_name }}</div>
                    </div>
                  </div>
                </template>
                <template v-else-if="label==='Brands'">{{ item.brand_name }}</template>
                <template v-else>{{ item.display_name ? item.display_name : '---' }}</template>
              </td>
              <td v-if="label !== 'Creators'">
                <div class="creators-row">
                  <svg-icon name="users-2" />
                  {{ item.creator_count != null ? item.creator_count.toLocaleString('en-US') : '---' }}
                </div>
              </td>
              <td>{{ item.total_products != null ? item.total_products.toLocaleString('en-US') : '---' }}</td>
              <td v-if="show_views">{{ item.views ? item.views.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.click ? item.click.toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.sales ? item.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="typeof item.sales_value === 'number'">
                ${{ item.sales_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="typeof item.earnings === 'number'">
                ${{ item.earnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="label === 'Creators'">{{ item.last_update ? formatDate(item.last_update) : '---' }}</td>
            </tr>
          </template>

          <!-- <template #foot>
            <tr class="total-row" v-if="data_total && label !== 'Advertisers'">
              <td class="name-row">Total: {{ total.toLocaleString('en-US') }}</td>
              <td v-if="label !== 'Creators'">{{ data_total.creator_count != null ? data_total.creator_count.toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.total_products != null ? data_total.total_products.toLocaleString('en-US') : '---' }}</td>
              <td v-if="show_views">{{ data_total.views ? data_total.views.toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.click ? data_total.click.toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.sales ? data_total.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="typeof data_total.sales_value === 'number'">
                ${{
                  data_total.sales_value.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td v-else>---</td>
              <td v-if="typeof data_total.earnings === 'number'">
                ${{
                  data_total.earnings.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}
              </td>
              <td v-else>---</td>
              <td v-if="label === 'Creators'">---</td>
            </tr>
          </template> -->
        </Table>
      </div>

      <div v-if="!is_loading && data.length === 0">
        <NoData :image="require('@/assets/images/pc.png')" title="No data available" text="" />
      </div>
    </Scrollbar>
  </section>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useAnalyticsProductList, sort_items, per_page_items } from '@/composables/useAnalyticsProductList.js';

const formatDate = (val) => {
  if (!val) return '---';
  const d = new Date(val);
  return isNaN(d.getTime()) ? '---' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
import Scrollbar from '@/components/ui/Scrollbar.vue';
import Search from '@/components/ui/Search.vue';
import Dropdown from '@/components/ui/Dropdown.vue';
import DropdownBrands from '@/components/ui/DropdownBrands.vue';
import DropdownAd from '@/components/ui/DropdownAd.vue';
import Checkbox from '@/components/ui/Checkbox.vue';
import FilterDropdown from '@/components/FilterDropdown.vue';
import Pagenavi from '@/components/ui/Pagenavi.vue';
import Loading from '@/components/ui/Loading.vue';
import NoData from '@/components/creator/NoData.vue';
import Table from '@/components/ui/Table.vue';
import TableTh from '@/components/ui/TableTh.vue';
import SValue from '@/components/summary/SummaryValue.vue';
import { ElButton } from 'element-plus';
import { getCurrentInstance } from 'vue';
import { useStore } from 'vuex';

const emit = defineEmits(['open-detail']);

const props = defineProps({
  label: { type: String, required: true },
  search_placeholder: { type: String, default: 'Search' },
  build_statistic_url: { type: Function, required: true },
  build_list_url: { type: Function, required: true },
  brand_filter_items: { type: Array, default: () => [] },
  start_date: { type: String, default: '' },
  end_date: { type: String, default: '' },
  creator_type_items: { type: Array, default: () => [] },
  hide_curated: { type: Boolean, default: false },
  hide_sort: { type: Boolean, default: false },
});

const { proxy } = getCurrentInstance();
const EventBus = proxy.EventBus;
const store = useStore();
const {
  is_loading, data, data_statistic, data_total,
  current_page, last_page, total,
  sort, sort_index, sortlink_data, curated_check,
  creator_type, product_added, account_status,
  is_link_only, now_merchant,
  onSearch, onSortChange, onColumnSort, onBrandsChange, onAdvertisersChange, onCreatorTypeChange,
  onProductAddedChange, onAccountStatusChange,
  toggleCurated, onPerPageChange, gotoPage,
} = useAnalyticsProductList({
  buildStatisticUrl: props.build_statistic_url,
  buildListUrl: props.build_list_url,
  // Page-level table (Brands.vue / Advertisers.vue): follows AnalyticsHeader's Creator Tag filter
  useGlobalCreatorTags: true,
});

// Consistent with show_views logic in Overview.vue / ProductList.vue:
// Hide Product Views when Shortlink Data is selected or for link-only merchants (no historical storefront tracking)
const show_views = computed(() => {
  if (sortlink_data.value === 'shortlink') return false;
  return !is_link_only.value || (is_link_only.value && store.state.list_has_historical_storefront_tracking);
});

const filter_params = reactive({
  profile_status: 0,
  storefront_status: 0,
  recently_added: 0,
  metric: 0,
  metric_type: 0,
  metric_amount: '',
  metric_min_amount: '',
  metric_max_amount: '',
  account_type: 0,
  social: { metric: 1, metric_msg: '', metric_min_amount_input: '', metric_max_amount_input: '' },
});

const _onUpdateByBrands = (brands) => onBrandsChange(brands);
const _onUpdateByAd = (advertisers) => onAdvertisersChange(advertisers);

onMounted(() => {
  EventBus.on('update-by-brands', _onUpdateByBrands);
  EventBus.on('update-by-ad', _onUpdateByAd);
});

onBeforeUnmount(() => {
  EventBus.off('update-by-brands', _onUpdateByBrands);
  EventBus.off('update-by-ad', _onUpdateByAd);
});

const updateFilter = (params) => {
  Object.assign(filter_params, params);
  onProductAddedChange(params.recently_added === 1 ? 'recently_added' : '');
  onAccountStatusChange({ 1: 'active', 3: 'disabled' }[params.storefront_status] ?? '');
  onCreatorTypeChange({ value: { 1: 'storefront', 2: 'linkonly' }[params.account_type] ?? '' });
};

const openDetail = (item) => {
  const detail_item = {
    ...item,
    start_date: store.state.save_analytics_start_date,
    end_date: store.state.save_analytics_end_date,
  };
  if (props.label === 'Advertisers') {
    EventBus.emit('open-advertiser-detail', detail_item);
  } else if (props.label === 'Brands') {
    EventBus.emit('open-brand-detail', detail_item);
  }
  emit('open-detail', detail_item);
};
</script>

<style lang="scss">
.analytics-product-table {
  padding-bottom: 24px;

  .sticky-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 20;
  }

  .list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 0 8px;

    .list-btn-box {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
    }


    .the-curated-btn {
      border-color: var(--border);
      color: var(--black-60);
    }
  }

  .ui-search-wrap {
    flex: 1;

    .ui-search-input .input {
      max-width: unset;
    }
  }

  .short-link {
    margin: 12px 0;

    .report-box {
      display: flex;
      position: relative;
      border: 1px solid rgba(0, 0, 0, 0.08);
      margin: 0 0 16px;

      .report-item {
        flex: 1;
        padding: 12px;
        border-right: 1px solid rgba(0, 0, 0, 0.08);

        &:last-child {
          border-right: none;
        }

        .datainfo-wrap {
            .text {
              font:var(--font-med-14);
              color: var(--text-secondary);
              margin-bottom: 4px;
            }

            .desc-text{
              font:var(--font-med-12);
            }
            .value {
              font: var(--font-med-20);
              color: var(--dark-blue);
            }
        }


        .the-type {
          width: 100%;
          border: none;
          padding: 0;
          background: transparent;
          margin-top: 8px;
        }
      }
    }
  }

  .data-list-scroll {
    overflow-y: visible;
    height: calc(98vh - 360px);
  }

  .product-table-view {
    margin-top: 8px;
    cursor: pointer;
    position: relative;

    .ui-table thead {
      position: sticky;
      top: 0;
      background: white;
      z-index: 10;
    }

    .ui-table td{
      padding: 16px 12px;
    }

    .name-row {
      min-width: 180px;

      .product-name-cell {
        display: flex;
        align-items: center;
        gap: 8px;

        .product-thumb {
          width: 36px;
          height: 36px;
          border-radius: 4px;
          flex-shrink: 0;
          background: #e0e0e0;
        }

        .product-info {
          min-width: 0;

          .product-title {
            font-size: 13px;
            font-weight: 500;
            color: var(--black-90);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .product-brand {
            font-size: 11px;
            color: var(--black-50);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    .creators-row {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    svg {
      width: 12px;
      height: 12px;
      margin-right: 4px;
      vertical-align: middle;
    }
  }

  .tfoot tr td {
    vertical-align: top;
  }
}
</style>
